import React, { PropTypes as T } from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form/immutable';
import _ from 'lodash';

import { setReloadDiscussions } from '../../containers/discussion-list/actions';
import AxiosService from '../../utils/axios-service';
import { authService } from '../../utils/auth-service';
import { INFLUENCER } from '../../utils/static-configuration';
import { setStatusBar } from '../../containers/status-bar/actions';
import FormInviteRow from '../../components/form-invite-row';
import { EMAIL_REGULAR_EXPRESSION } from '../../utils/form-input-validation';
import { clearStatusBar } from '../../containers/status-bar/actions';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';

class CampaignInvite extends React.Component {
  static contextTypes = {
    router: T.object,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      resetStatusBar: false,
      campaignInviteLoading: false,
    };
  }

  changeAddClientState(currentSelect) {
    this.setState({ addClientState: currentSelect });
  }

  componentWillUnmount() {
    this.props.reset(); // Reset form values

    // Reset status bar if required
    if (this.state.resetStatusBar) {
      this.props.onClearStatusBar();
    }
  }

  inviteCollaborators(formData) {
    this.setState({ campaignInviteLoading: true });

    const { campaignId, campaignName } = this.props;
    const collaboratorInsertList = [];

    formData.forEach(collaboratorList => {
      const collaboratorInsert = {};

      collaboratorList.forEach(collaborator => {
        const email = collaborator.get('email');
        const role = collaborator.get('role');
        const name = collaborator.get('name');
        const { origin } = window.location;
        collaboratorInsert.link = `${origin}/invite/`;
        collaboratorInsert.type = 'campaignInvitation';
        collaboratorInsert.status = 'sent';
        collaboratorInsert.campaign = {
          type: 'campaigns',
          id: campaignId,
        };
        collaboratorInsert.campaignName = campaignName;
        collaboratorInsert.email = email;
        collaboratorInsert.role = role;
        collaboratorInsert.name = name;

        if (role === INFLUENCER) {
          const socialHandle = collaborator.get('socialHandle');
          const platforms = [];
          if (collaborator.get('facebook')) platforms.push('facebook');
          if (collaborator.get('twitter')) platforms.push('twitter');
          if (collaborator.get('youtube')) platforms.push('youtube');
          collaboratorInsert.metadata = { socialHandle, platforms };
        }

        if (collaboratorInsert.email && collaboratorInsert.role) {
          collaboratorInsertList.push(_.assign({}, collaboratorInsert));

          const inviteMemberData = { campaignId, inviteEmail: email, inviteRole: role };
          if (name) inviteMemberData.inviteName = name;
          trackEvent('Invite Member', inviteMemberData);
        }
      });
    });

    const axiosService = new AxiosService(process.env.REACT_APP_BATCH_CREATE_CAMPAIGN_INVITATION_API, 10000, { Authorization: `Bearer ${authService.getToken()}` });

    axiosService.post(`/${campaignId}`, { data: collaboratorInsertList })
      .then(() => {
        this.props.onSetReloadDiscussions(true);
        this.context.router.push(`/campaign/${campaignId}`);
      })
      .catch(error => {
        reportError('INVITE_COLLABORATOR_ERROR', error.message);
        this.props.onSetStatusBar(error.message, 'red');
        this.setState({ resetStatusBar: true });
      });
  }

  render() {
    const { handleSubmit, campaign, invalid, submitFailed, collaboratorsForm } = this.props;
    const { campaignInviteLoading } = this.state;

    const hasClient = !!campaign.get('client');

    return (
      <form onSubmit={handleSubmit(this.inviteCollaborators.bind(this))}>

        <FieldArray
          name="collaborators"
          form={collaboratorsForm}
          hasClient={hasClient}
          component={FormInviteRow}
        />

        <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
          <div className="hero small">
            <p>Any people you add will get an email with instructions to join this dyzio campaign.</p>
            <div className="spacer" />
            <button className="large rose wide" type="submit">
              { campaignInviteLoading
                ? <ul className="dots">
                  <li id="dot1" />
                  <li />
                  <li id="dot2" />
                  <li />
                  <li id="dot3" />
                </ul>
                : <p>invite and continue</p>
              }
            </button>
            { invalid && submitFailed &&
              <div>
                <p className="small error">
                  Errors found above. Please correct them and try again.
                </p>
                <div className="spacer" />
              </div>
            }
          </div>
        </div>
      </form>
    );
  }
}

CampaignInvite.propTypes = {
  routeParams: React.PropTypes.object,
};

function validate(values, props) {
  const errors = {};
  const collaborators = values.get('collaborators');
  const collaboratorsListErrors = [];
  const allEmails = [];
  const memberEmails = props.memberEmails;
  const pendingMemberEmails = props.pendingMemberEmails;

  collaborators.forEach((collaborator, index) => {
    const email = collaborator.get('email');
    const role = collaborator.get('role');
    const collaboratorErrors = {};

    const emailExists = _.find(allEmails, existingEmail => existingEmail === email);
    if (emailExists) {
      collaboratorErrors.email = 'Emails must be unique';
      collaboratorsListErrors[index] = collaboratorErrors;
    }
    allEmails.push(email);

    const memberExists = _.includes(memberEmails, email);
    if (memberExists) {
      collaboratorErrors.email = 'This person is already on this campaign.';
      collaboratorsListErrors[index] = collaboratorErrors;
    }

    const pendingMemberExists = _.includes(pendingMemberEmails, email);
    if (pendingMemberExists) {
      collaboratorErrors.email = 'This person has already been invited.';
      collaboratorsListErrors[index] = collaboratorErrors;
    }

    // If an attempt has been made to fill in the form row validate it
    if (email || role) {
      if (!email) {
        collaboratorErrors.email = 'Please enter a valid email.';
        collaboratorsListErrors[index] = collaboratorErrors;
      }
      if (email && !EMAIL_REGULAR_EXPRESSION.test(email)) {
        collaboratorErrors.email = 'Please enter a valid email.';
        collaboratorsListErrors[index] = collaboratorErrors;
      }
      if (!role) {
        collaboratorErrors.role = 'Please select a collaborator role.';
        collaboratorsListErrors[index] = collaboratorErrors;
      }

      const socialHandle = collaborator.get('socialHandle');
      const influencer = collaborator.get('influencer');
      const company = collaborator.get('company');

      if (role === INFLUENCER) {
        if (!socialHandle) {
          collaboratorErrors.socialHandle = 'Please enter a social handle.';
          collaboratorsListErrors[index] = collaboratorErrors;
        }
      }
    }

    if (collaboratorsListErrors.length) {
      errors.collaborators = collaboratorsListErrors;
    }
  });

  return errors;
}

// Used to map the form state to props to allow for custom form render logic
const selector = formValueSelector('InviteCampaignForm');

const mapStateToProps = state => ({
  campaign: state.campaign.get('campaign'),
  currentUser: state.userData.get('currentUser'),
  collaboratorsForm: selector(state, 'collaborators'),
  initialValues: {
    collaborators: [{}, {}, {}],
  },
});

export function mapDispatchToProps(dispatch) {
  return {
    onClearStatusBar: () => dispatch(clearStatusBar()),
    onSetStatusBar: (message, colour) => dispatch(setStatusBar(message, colour)),
    onSetReloadDiscussions: reload => dispatch(setReloadDiscussions(reload)),
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'InviteCampaignForm',
    validate,
  })(CampaignInvite),
);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-new/campaign-invite.js
 **/